import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';

import SuiteEnum from 'redpoints-model-js-common/enumeration/SuiteEnum';
import IncidentStatusEnum from 'redpoints-model-js/enumeration/IncidentStatusEnum';
import IncidentDiscardedReason from 'redpoints-model-js/model/incident/IncidentDiscardedReason';
import SetIncidentStatusObject from 'redpoints-model-js/model/incident/SetIncidentStatusObject';
import DetectionLink from 'redpoints-model-js/model/system/DetectionLink';
import IncidentByUrls from 'redpoints-model-js/model/system/IncidentByUrls';

import HttpStatus from 'redpoints-connector-common/enumeration/HttpStatus';
import Context from 'redpoints-connector-common/model/Context';
import AssetController from 'rpc-restapi-connector/controller/AssetController';
import ContractController from 'rpc-restapi-connector/controller/ContractController';
import DetectionLinkController from 'redpoints-restapi-connector/controller/DetectionLinkController';
import IncidentByUrlCreationController from 'redpoints-restapi-connector/controller/IncidentByUrlCreationController';
import IncidentToReviewTaskController from 'redpoints-restapi-connector/controller/IncidentToReviewTaskController';
import MoveIncidentToNewStatusController from 'redpoints-restapi-connector/controller/MoveIncidentToNewStatusController';

import { Notification } from 'redpoints-front-components/index';
import {
  buildDomainsPlatformsTree,
  filterProtectionTypesWithTranslations,
} from 'redpoints-front-common/helpers';

import { translate } from '../../../helpers/languageHelper';

const DOMAIN_PLATFORM = 'domainPlatform';
const DOMAIN_ID = 'domainId';
const TREE_DELIMITER = '/';

/**
 *
 * @param assetId
 * @param assetList
 * @returns {{customerId: *, contractId: *}}
 */
const customerAndContractByAsset = (assetId, assetList) => {
  const assetSelected = assetList.find(asset => asset.assetId === assetId);
  return {
    customerId: get(assetSelected, 'contract.customer.customerId', null),
    contractId: get(assetSelected, 'contract.contractId', null),
  };
};

const createByUrl = async ({
  assetId,
  context,
  contractId,
  customerId,
  incidentBody,
}) => {
  const newContext = Context.build(context);
  newContext.customerId = customerId;
  newContext.contractId = contractId;

  const full = false;
  const createByUrlResponse = await IncidentByUrlCreationController.createByUrls(
    newContext,
    assetId,
    full,
    incidentBody
  );

  const created = createByUrlResponse.status === HttpStatus.CREATED.value;
  if (!created && createByUrlResponse.error) {
    Notification.error({
      message: `Error ${createByUrlResponse.status}`,
      description: `${createByUrlResponse.error.message}`,
    });
  }

  return created;
};

const getProtectionTypes = async context => {
  const full = true;
  const resp = await ContractController.get(context, full);
  return resp.status === HttpStatus.OK.value ? resp : [];
};

const setProtectionType = async ({
  assetId,
  context,
  contractId,
  customerId,
  detectionLinkBody,
  detectionLinkId,
}) => {
  const newContext = Context.build(context);
  newContext.customerId = customerId;
  newContext.contractId = contractId;

  const detectionLinkResponse = await DetectionLinkController.update(
    newContext,
    assetId,
    detectionLinkId,
    detectionLinkBody
  );

  return detectionLinkResponse.status === HttpStatus.OK.value;
};

const setIncidentStatus = async ({
  context,
  assetId,
  incidentId,
  incidentStatusEnum,
  discardReason,
}) => {
  const setIncidentStatusObject = new SetIncidentStatusObject();
  setIncidentStatusObject.incidentStatus = incidentStatusEnum;
  setIncidentStatusObject.incidentDiscardedReason = discardReason;

  return await MoveIncidentToNewStatusController.move(
    context,
    assetId,
    incidentId,
    setIncidentStatusObject
  );
};

/**
 *
 * @param context
 * @param customerId
 * @param contractId
 * @param incidentOnReview
 * @param incidentForm
 * @returns {Promise<boolean>}
 */
const updateDetectionLink = async ({
  context,
  contractId,
  customerId,
  incidentFormValues,
  incidentOnReview,
}) => {
  const isProtectionTypeTouched = !isEqual(
    incidentOnReview.protectionType,
    incidentFormValues.protectionType?.protectionType ||
      incidentFormValues.protectionType
  );
  const { incident } = incidentOnReview;
  const detectionLinkId = get(incident, 'detectionLink.detectionLinkId', null);
  let linkIsUpdated = false;

  if (isProtectionTypeTouched) {
    const detectionLinkBody = new DetectionLink();
    detectionLinkBody.url = incidentFormValues.detectionLink;
    detectionLinkBody.infringementProtectionType =
      incidentFormValues.protectionType;

    linkIsUpdated = await setProtectionType({
      assetId: incidentFormValues.asset?.assetId || incidentFormValues.asset,
      context,
      contractId,
      customerId,
      detectionLinkBody,
      detectionLinkId,
    });
  }

  return linkIsUpdated;
};
/**
 *
 * @param incidentOnReview
 * @param reportedIncidentsBaseParams
 * @param status
 * @returns {Promise<boolean>}
 * @constructor
 */
const setOldIncidentStatus = async ({
  context,
  incidentOnReview,
  reportedIncidentsBaseParams,
  status,
  discardedReasonId,
}) => {
  const oldCustomerContract = customerAndContractByAsset(
    incidentOnReview.asset.assetId,
    incidentOnReview.assetList
  );
  const paramsWithOldIncident = {
    ...reportedIncidentsBaseParams,
    contractId: oldCustomerContract.contractId,
    customerId: oldCustomerContract.customerId,
    assetId: incidentOnReview.asset.assetId,
  };

  const discardReason = new IncidentDiscardedReason();
  if (discardedReasonId) {
    discardReason.incidentDiscardedReasonId = discardedReasonId;
  }

  // Set Status tu incident discarded
  return await handleIncidentStatus({
    context,
    reportedIncidentsParams: paramsWithOldIncident,
    status,
    discardReason,
  });
};
/**
 *
 * @param incidentOnReview
 * @param incidentForm
 * @returns {boolean}
 */
const checkLinkFieldsChanged = ({ incidentOnReview, incidentFormValues }) => {
  const priorityProps = ['detectionLink', 'contentLinkText'];
  return priorityProps.some(
    prop => !isEqual(incidentOnReview[prop], incidentFormValues[prop])
  );
};

const mapStoreToForm = ({
  asset,
  protectionType,
  detectionLink = '',
  contentLinkText = '',
}) => ({
  asset: { assetId: asset?.assetId },
  protectionType,
  detectionLink,
  contentLinkText,
});

/**
 *
 * @param context
 * @param reportedIncidents
 * @returns {{scope: *, customerId: *, contractId: *, assetId: *, incidentId: *}}
 */
const mapReportedIncidentToParams = (context, reportedIncidents) => ({
  scope: context.scope,
  customerId: reportedIncidents.incident?.customerId,
  contractId: reportedIncidents.incident?.contractId,
  assetId: reportedIncidents.assetId,
  incidentId: reportedIncidents.incidentId,
});

const createReportedIncidentState = ({
  data,
  protectionTypes = {},
  protectionType = null,
  assetList = [],
}) => ({
  incident: data,
  incidentId: data.incidentId,
  createdBy: data.createdBy,
  assetId: data.asset.assetId,
  protectionTypesList: protectionTypes || null,
  protectionType: protectionType,
  asset: {
    name: data.asset.name,
    assetId: data.asset.assetId,
  },
  assetList: assetList.map(({ name, assetId, contract }) => ({
    name,
    assetId,
    contract,
  })),
  priority: data.detectionLink?.priority,
  detectionLink: get(data, 'detectionLink.url', ''),
  contentLinkText: get(data, 'contentLink.url', ''),
});

/**
 *
 * @param currentFormValues
 * @param prevFormState
 * @returns {boolean|*}
 */
const reportedIncidentIsEquals = (currentFormValues, prevFormState) => {
  const storeForm = prevFormState && mapStoreToForm(prevFormState);
  const formValues = currentFormValues && mapStoreToForm(currentFormValues);

  return isEqual(formValues, storeForm);
};
/**
 *
 * @param context
 * @param domainList
 * @param platformList
 * @returns {Promise<void>}
 */
const getReportedIncident = async ({
  context,
  domainList,
  domainPlatformList,
  customerId,
}) => {
  let reportedIncidents = {};
  const full = true;
  const incident = await IncidentToReviewTaskController.getNextToReview(
    context,
    full,
    domainList,
    domainPlatformList,
    customerId
  );

  // Error getting links
  if (incident.status === HttpStatus.NOT_FOUND.value) {
    // No more links
    reportedIncidents = {};
  } else if (incident.error) {
    Notification.error({
      description: translate('tasks.reported_incidents.error.getNextIncident'),
    });
  } else if (incident.status === HttpStatus.OK.value) {
    const {
      response: {
        contractId,
        customerId,
        detectionLink: { infringementProtectionType },
      },
    } = incident;
    const newContext = cloneDeep(context);
    newContext.contractId = contractId;
    newContext.customerId = customerId;

    const {
      response: { protectionTypes },
    } = await getProtectionTypes(newContext);

    const protectionTypesArray = protectionTypes.map(
      ({ contractProtectionTypeId, protectionType }) => ({
        ...protectionType,
        contractProtectionTypeId,
      })
    );

    const filteredProtectionTypes = filterProtectionTypesWithTranslations(
      protectionTypesArray,
      SuiteEnum.IPR.value
    );

    const assetList = await AssetController.list(newContext, true);

    const protectionType = filteredProtectionTypes.find(
      item => item.protectionType === infringementProtectionType.name
    );
    const protectionTypesList = filteredProtectionTypes.map(item => ({
      contractProtectionTypeId: item.contractProtectionTypeId,
      protectionType: item.protectionType,
      name: translate(`global.protectiontype.${item.protectionType}`),
    }));

    reportedIncidents = createReportedIncidentState({
      data: incident.response,
      protectionTypes: protectionTypesList,
      protectionType,
      assetList: assetList.response || [],
    });
  }
  return reportedIncidents;
};
/**
 *
 * @param context
 * @param assetId
 * @param incidentId
 * @param status
 * @returns {Promise<boolean>}
 */
const handleIncidentStatus = async ({
  context,
  reportedIncidentsParams: { customerId, contractId, assetId, incidentId },
  status = IncidentStatusEnum.ASSET_VALIDATED,
  discardReason = null,
}) => {
  const newContext = Context.build(context);
  newContext.customerId = customerId;
  newContext.contractId = contractId;
  return await setIncidentStatus({
    context: newContext,
    assetId,
    incidentId,
    incidentStatusEnum: status,
    discardReason,
  });
};
/**
 *
 * @param context
 * @param reportedIncidents
 * @returns {Promise<boolean>}
 */
const handleReportedIncidentsSubmit = async (
  context,
  reportedIncidents,
  discardedReasonIds
) => {
  let response;
  const { scope } = context;
  const incidentOnReview = reportedIncidents.onReview;
  const incidentFormValues = reportedIncidents.form.getFieldsValue();
  const { incidentId } = incidentOnReview;
  const { customerId, contractId } = customerAndContractByAsset(
    incidentFormValues.asset?.assetId || incidentFormValues.asset,
    incidentOnReview.assetList
  );

  const areLinkFieldsChanged = checkLinkFieldsChanged({
    incidentOnReview,
    incidentFormValues,
  });

  const areAssetFieldChanged =
    incidentOnReview.asset !== incidentFormValues.asset;

  const arePriorityFieldsTouched = areLinkFieldsChanged || areAssetFieldChanged;

  const reportedIncidentsBaseParams = {
    scope,
    customerId,
    contractId,
    incidentId,
    assetId: incidentFormValues.asset?.assetId || incidentFormValues.asset,
  };

  if (!arePriorityFieldsTouched) {
    const isLinkUpdate = await updateDetectionLink({
      ...reportedIncidentsBaseParams,
      context,
      incidentOnReview,
      incidentFormValues,
    });
    if (isLinkUpdate) {
      // Accept this incident
      response = await handleIncidentStatus({
        context,
        reportedIncidentsParams: reportedIncidentsBaseParams,
        status: IncidentStatusEnum.ASSET_VALIDATED,
      });

      if (response.status !== HttpStatus.NO_CONTENT.value) {
        return false;
      }
    }
  } else {
    const discardedReasonId = areLinkFieldsChanged
      ? discardedReasonIds?.incorrectFormatId
      : discardedReasonIds?.incorrectAssetId;

    // Discard Old incident
    let isUrlCreated = false;
    response = await setOldIncidentStatus({
      context,
      incidentOnReview,
      reportedIncidentsBaseParams,
      status: IncidentStatusEnum.INCIDENT_DISCARDED,
      discardedReasonId,
    });

    if (response.status !== HttpStatus.NO_CONTENT.value) return false;

    const incidentBody = new IncidentByUrls();
    incidentBody.protectionType =
      incidentFormValues.protectionType?.protectionType ||
      incidentFormValues.protectionType;
    incidentBody.detectionLinkUrl = incidentFormValues.detectionLink;
    incidentBody.incidentLinkUrl = incidentFormValues.contentLinkText;
    incidentBody.detectionLinkReporter =
      incidentOnReview.incident.detectionLink.reporter;
    incidentBody.incidentLinkReporter =
      incidentOnReview.incident.contentLink?.reporter ?? null;

    isUrlCreated = await createByUrl({
      ...reportedIncidentsBaseParams,
      context,
      incidentBody,
    });

    if (!isUrlCreated) return false;
  }
  return true;
};

const getDomainsIdFromTree = ({ domainIds }) => {
  const domainPlatformList = [];
  const domainList = [];

  domainIds.forEach(element => {
    const [domainType, domainSelected] = element.split(TREE_DELIMITER);

    if (domainType === DOMAIN_PLATFORM) {
      domainPlatformList.push(domainSelected);
    } else if (domainType === DOMAIN_ID) {
      domainList.push(domainSelected);
    }
  });

  return {
    domainList,
    domainPlatformList,
  };
};

const getDomainPlatform = async ({ context, customerId }) => {
  let domainList = [];
  const domainListResponse = await IncidentToReviewTaskController.listDomainPlatformForIncidentToReview(
    context,
    customerId
  );

  if (domainListResponse && domainListResponse.status === HttpStatus.OK.value) {
    domainList = buildDomainsPlatformsTree(domainListResponse.response);
  } else {
    Notification.error({
      message: translate('tasks.reported_incidents.error.domainList'),
    });
  }

  return domainList;
};

export {
  handleReportedIncidentsSubmit,
  mapReportedIncidentToParams,
  createReportedIncidentState,
  reportedIncidentIsEquals,
  getReportedIncident,
  mapStoreToForm,
  handleIncidentStatus,
  getDomainPlatform,
  getDomainsIdFromTree,
};
